import Avatar from '@/components/linear/avatar';
import PriorityIcon from '@/components/linear/priority-icon';
import StatusIcon from '@/components/linear/status-icon';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import { saveDraft } from '@/lib/drafts';
import { issuesPath } from '@/lib/linear-paths';
import { PRIORITY_LABELS, resolveTeams, STATUS_LABELS } from '@/lib/linear-utils';
import type { IssueStatus } from '@/types/issue';
import type { Project } from '@/types/project';
import type { SharedPageProps } from '@/types/shared';
import { useForm, usePage } from '@inertiajs/react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface MemberOption {
  id: string;
  name: string;
  avatarColor?: string;
}

interface IssueDraftFields {
  title: string;
  description: string;
  teamId: string;
  status: IssueStatus;
  assigneeId: string;
  priority: number;
  projectId: string;
}

export default function CreateIssueModal() {
  const { createIssueOpen, setCreateIssueOpen } = useWorkspaceChrome();
  const {
    teams,
    currentCompany,
    currentUser,
    projects = [],
    members = [],
  } = usePage<
    SharedPageProps & {
      projects?: Project[];
      members?: MemberOption[];
    }
  >().props;
  const resolvedTeams = resolveTeams(teams, currentCompany);
  const titleRef = useRef<HTMLInputElement>(null);
  const defaultTeamId = resolvedTeams[0]?.id ?? '';
  const defaultAssigneeId = currentUser?.id ?? '';
  const [createMore, setCreateMore] = useState(false);
  const defaultsRef = useRef({ teamId: defaultTeamId, assigneeId: defaultAssigneeId });
  defaultsRef.current = { teamId: defaultTeamId, assigneeId: defaultAssigneeId };
  const draftRef = useRef({ title: '', description: '', teamId: defaultTeamId });

  const assigneeOptions =
    currentUser && !members.some((member) => member.id === currentUser.id)
      ? [
          {
            id: currentUser.id,
            name: currentUser.name,
            avatarColor: currentUser.avatarColor,
          },
          ...members,
        ]
      : members;

  const freshIssue = useCallback(
    (): IssueDraftFields => ({
      title: '',
      description: '',
      teamId: defaultsRef.current.teamId,
      status: 'todo',
      assigneeId: defaultsRef.current.assigneeId,
      priority: 0,
      projectId: '',
    }),
    []
  );

  const form = useForm<IssueDraftFields>({
    title: '',
    description: '',
    teamId: defaultTeamId,
    status: 'todo',
    assigneeId: defaultAssigneeId,
    priority: 0,
    projectId: '',
  });

  draftRef.current = {
    title: form.data.title,
    description: form.data.description,
    teamId: form.data.teamId,
  };

  const selectedAssignee =
    assigneeOptions.find((member) => member.id === form.data.assigneeId) ?? null;

  useEffect(() => {
    if (createIssueOpen) {
      const next = freshIssue();
      form.setDefaults(next);
      form.setData(next);
      form.clearErrors();
      requestAnimationFrame(() => titleRef.current?.focus());
    } else {
      setCreateMore(false);
    }
  }, [createIssueOpen, freshIssue]);

  const closeModal = useCallback(() => {
    const draft = draftRef.current;
    if (draft.title.trim() || draft.description.trim()) {
      saveDraft({
        title: draft.title,
        description: draft.description,
        teamId: draft.teamId,
      });
    }
    setCreateIssueOpen(false);
  }, [setCreateIssueOpen]);

  const submit = useCallback(() => {
    if (!form.data.title.trim() || !form.data.teamId) return;
    form.transform((data) => ({ issue: data }));
    form.post(issuesPath(), {
      onSuccess: () => {
        if (createMore) {
          const next = { ...form.data, title: '', description: '' };
          // setDefaults stops Inertia from putting the submitted title back.
          form.setDefaults(next);
          form.setData(next);
          requestAnimationFrame(() => titleRef.current?.focus());
        } else {
          setCreateIssueOpen(false);
        }
      },
    });
  }, [createMore, form, setCreateIssueOpen]);

  useEffect(() => {
    if (!createIssueOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        submit();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeModal, createIssueOpen, submit]);

  if (!createIssueOpen) return null;

  return (
    <div
      className="ln-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="New issue"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          closeModal();
        }
      }}
    >
      <form
        className="ln-modal"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="ln-modal-top">
          <label className="ln-chip">
            <select
              value={form.data.teamId}
              onChange={(event) => form.setData('teamId', event.target.value)}
              aria-label="Team"
            >
              {resolvedTeams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.key || team.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <input
          ref={titleRef}
          className="ln-modal-title"
          value={form.data.title}
          onChange={(event) => form.setData('title', event.target.value)}
          placeholder="Issue title"
          aria-label="Issue title"
        />
        {form.errors.title ? (
          <div className="ln-error" style={{ padding: '0 20px' }}>
            {form.errors.title}
          </div>
        ) : null}
        <textarea
          className="ln-modal-body"
          value={form.data.description}
          onChange={(event) => form.setData('description', event.target.value)}
          placeholder="Add description…"
          aria-label="Issue description"
        />
        <div
          className="ln-modal-footer"
          style={{ borderTop: 'none', paddingTop: 0, flexWrap: 'wrap' }}
        >
          <label className="ln-chip">
            <StatusIcon status={form.data.status} />
            <select
              value={form.data.status}
              onChange={(event) => form.setData('status', event.target.value as IssueStatus)}
              aria-label="Status"
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="ln-chip">
            <PriorityIcon priority={form.data.priority} />
            <select
              value={String(form.data.priority)}
              onChange={(event) => form.setData('priority', Number(event.target.value))}
              aria-label="Priority"
            >
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="ln-chip">
            {selectedAssignee ? (
              <Avatar name={selectedAssignee.name} color={selectedAssignee.avatarColor} size={16} />
            ) : (
              <span
                aria-hidden="true"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '1.5px solid var(--ln-faint)',
                  flexShrink: 0,
                }}
              />
            )}
            <select
              value={form.data.assigneeId}
              onChange={(event) => form.setData('assigneeId', event.target.value)}
              aria-label="Assignee"
            >
              <option value="">Unassigned</option>
              {assigneeOptions.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>
          <label className="ln-chip">
            <select
              value={form.data.projectId}
              onChange={(event) => form.setData('projectId', event.target.value)}
              aria-label="Project"
            >
              <option value="">No project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="ln-modal-footer">
          <button
            type="button"
            className="ln-chip"
            aria-pressed={createMore}
            onClick={() => setCreateMore((on) => !on)}
            style={{ marginLeft: 'auto' }}
          >
            {createMore ? '●' : '○'} Create more
          </button>
          <button
            type="submit"
            className="ln-primary-btn"
            style={{ marginLeft: 0 }}
            disabled={form.processing || !form.data.title.trim() || !form.data.teamId}
          >
            Create issue
          </button>
        </div>
      </form>
    </div>
  );
}
